<template>
  <div class="chart-container">
    <div v-if="dateFound" class="stackedTotals">
      <div>
        <h2 v-if="period == 'day'">Stacked Totals for {{ dayString }}</h2>
        <h2 v-if="period == 'week'">Stacked Totals for {{ weekString }}</h2>
        <h2 v-if="period == 'month'">Stacked Totals for {{ monthString }}</h2>
        <h2 v-if="period == 'year'">Stacked Totals for {{ yearString }}</h2>
        <span v-if="dataType == 'count'">Overall Total: {{ overallTotal }}</span>
        <span v-else>Overall Total: <CurrencyFormatter :value="overallTotal" :currency="companySettings.currency" /> </span>
        <div class="stacked-list">
          <div v-for="(value, key, idx) in totalCheck" :key="key" class="stacked-item">
            <details :key="key">
              <summary class="summary-row">
                <span class="cat-name">{{ key }}</span>
                <span v-if="dataType == 'count'" class="cat-value">{{ value }}</span>
                <span v-else><CurrencyFormatter class="cat-value" v-if="dataType == 'sales'" :value="value" :currency="companySettings.currency" /></span>
              </summary>
              <div class="expanded-content">
                <div class="sorting-icons">
                  <font-awesome-icon icon="fa-sort-up" size="xl" @click.stop.prevent="sortProducts('asc', idx)" />
                  <font-awesome-icon icon="fa-sort-down" size="xl" @click.stop.prevent="sortProducts('desc', idx)" />
                </div>
                <table class="inner-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="[prod, val] in Object.entries(stackedProducts[key] || {})" :key="prod">
                      <td>{{ prod }}</td>
                      <td v-if="dataType == 'sales'">
                        <span class="cat-value"><CurrencyFormatter :value="Number(val)" :currency="companySettings.currency" /></span>
                      </td>
                      <td v-else>
                        <span class="cat-value">{{ Number(val) }}</span>
                      </td>
                    </tr>
                    <tr v-if="!stackedProducts[key] || Object.keys(stackedProducts[key]).length === 0">
                      <td colspan="2">No breakdown available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
    <div v-if="dateFound" class="stacked-chart">
      <VChart :option="BarChartOptions" class="chart" autoresize></VChart>
    </div>
    <div v-if="loading" class="loading">
      <h2>Compiling Data...</h2>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import { GridComponent, TitleComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import VChart from "vue-echarts";
import { dayRange, reports } from "@monolith/helpers";
import { LoadingSpinner, CurrencyFormatter } from "@monolith/components";
import { mapGetters } from "vuex";

echarts.use([CanvasRenderer, LineChart, GridComponent, LegendComponent, TitleComponent, TooltipComponent]);

export default {
  name: "App",
  components: {
    VChart,
    LoadingSpinner,
    CurrencyFormatter,
  },
  emits: ["handleExportData"],
  data() {
    return {
      companyName: localStorage.getItem("company"),
      stackedData: [],
      labels: [],
      dataSet: [],
      series: [],
      xName: this.period == "day" ? "Hour" : "Days",
      yName: this.dataType == "sales" ? "$" : "Units Sold",
      catAxis: [],
      loading: false,
      stackedTotals: [],
      weekStackedTotals: [],
      dayString: "",
      weekString: "",
      monthString: "",
      yearString: "",
      stackedProducts: {},
      dateFound: "",
      overallTotal: 0,
      totalCheck: {},
    };
  },
  props: {
    site: {
      type: String,
      required: true,
    },
    dataType: {
      type: String,
      required: true,
    },
    period: {
      type: String,
      required: true,
    },
    day: {
      type: Array,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      companySettings: "companySettings",
    }),
    BarChartOptions() {
      return {
        xAxis: {
          name: this.xName,
          data: this.catAxis,
          type: "category",
          nameLocation: "middle",
          nameGap: 30,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
          },
        },
        yAxis: {
          type: "value",
          name: this.yName,
          nameLocation: "middle",
          nameRotate: 360,
          nameGap: 50,
          show: true,
          nameTextStyle: {
            color: "#3bafda",
            fontSize: 16,
            width: "100%",
          },
        },
        series: this.series,
        legend: {
          textStyle: {
            color: "#3bafda",
          },
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            // Use axis to trigger tooltip
            type: "shadow", // 'shadow' as default; can also be 'line' or 'shadow'
          },
        },
        grid: {
          containLabel: true,
        },
      };
    },
  },

  methods: {
    exportData() {
      let dataToExport = [];
      let dataToExport2 = [];
      // key: hour 8, value: {key: bagel: value: 166}
      for (let i = 0; i < this.stackedTotals.length; i++) {
        let value = this.stackedTotals[i].value ? this.stackedTotals[i].value || this.stackedTotals[i] : 0;
        dataToExport[i] = { key: this.stackedTotals[i].key, value: value };
      }

      for (let i = 0; i < Object.keys(this.stackedProducts).length; i++) {
        let key = Object.keys(this.stackedProducts)[i];
        for (let j in this.stackedProducts[key]) {
          dataToExport2.push({ key: j, value: this.stackedProducts[key][j] });
        }
      }

      this.$emit("handleExportData", { key: "stacked", value: dataToExport });
      this.$emit("handleExportData", { key: "stackedProducts", value: dataToExport2 });
    },
    // label the axis with the hours in the day
    labelAxis(data) {
      let catAxis = [];
      data.map((data1) => {
        if (!catAxis.includes(reports.utcFix(data1.key[0]))) {
          // to make sure they are not in utc
          catAxis.push(reports.utcFix(data1.key[0]));
        }
      });
      return catAxis;
    },

    // checks all the data to see which hours has a value assigned, to make sre that the stacked hours are correct
    getHoursWithData(data) {
      let hours = [];
      let timezoneOffset = reports.getTimeZoneOffset();
      data.forEach((data1) => {
        let original = parseInt(data1.key[0].substring(11, 13));
        let hour = original + timezoneOffset;
        let newHour = hour >= 24 ? hour - 24 : hour;
        if (!hours.includes(newHour)) {
          hours.push(newHour);
        }
      });

      return hours;
    },

    // key: ['2025-11-18T20:', 'Bagels Loose'], value: 52.5
    // used only for hours in a day
    populateDayData(data) {
      let temp = [];
      let temp2 = [];
      let hours = this.getHoursWithData(data);
      data.map((data1) => {
        // get the hour from utc to local nz hour
        let hour = parseInt(data1.key[0].substring(11, 13)) + reports.getTimeZoneOffset();
        let newHour = hour >= 24 ? hour - 24 : hour;

        // if the catergory is not there add it to the array
        if (!temp.includes(data1.key[1])) {
          // a temporary array for checking the category
          temp.push(data1.key[1]);
          let newData = [];
          hours.forEach((h) => {
            if (h == newHour) {
              newData.push({ key: h, value: data1.value.toFixed(2) });
            } else {
              // push a empty value to make sure that the graph lines up
              newData.push({ key: h, value: 0 });
            }
          });

          //push the value to the array that is used
          temp2.push({
            name: data1.key[1],
            data: newData,
            type: "bar",
            stack: "total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            label: {
              show: true,
            },
          });
        } else {
          // if the category is already there then push the value to the category
          temp2.forEach((m) => {
            // make sure to match the key with the cat
            if (m.name == data1.key[1]) {
              // the hour needs to match within the array, not just added to end
              let map = new Map(m.data.map((d) => [d.key, Number(d.value)]));
              let newVal = Number(data1.value);
              map.set(newHour, (map.get(newHour) || 0) + newVal);

              // convert map back to array
              m.data = Array.from(map, ([key, value]) => ({ key, value: value.toFixed(2) }));
            }
          });
        }
      });
      // return the whole data structure to be passed straight to the graph
      return temp2;
    },

    // buildDaySeries(data) {
    //   const hours = this.getHoursWithData(data);
    //   const categoryHourMap = new Map();

    //   data.forEach(({ key, value }) => {
    //     const category = key[1];
    //     const hour = adjustHourFromKey(key[0]); // small helper
    //     const cat = categoryHourMap.get(category) || new Map();
    //     cat.set(hour, (cat.get(hour) || 0) + Number(value));
    //     categoryHourMap.set(category, cat);
    //   });

    //   return Array.from(categoryHourMap.entries()).map(([name, hourMap]) => {
    //     const dataArr = hours.map((h) => ({ key: h, value: hourMap.get(h) || 0 }));
    //     return { name, data: dataArr, type: "bar", stack: "total" /* ... */ };
    //   });
    // },

    populateData(data) {
      // data needs to be {key: Date Object, value: array of {key: category, value: int }}
      const seriesMap = [];
      // const seriesMap = new Map(); // retunr an array??

      data.forEach((day) => {
        day.value.forEach((item) => {
          const category = item.key;
          const value = Number(item.value.toFixed(2));
          const dayKey = day.key;

          // If the category hasn't been added yet, create its series
          if (!seriesMap[category]) {
            seriesMap[category] = {
              name: category,
              type: "bar",
              stack: "total",
              areaStyle: {},
              emphasis: { focus: "series" },
              label: { show: true },
              data: data.map((d) => ({ key: d.key, value: 0 })),
            };
          }

          // Update the corresponding day's value
          const targetDay = seriesMap[category].data.find((d) => d.key === dayKey);
          if (targetDay) {
            targetDay.value += value;
          }
        });
      });

      return Object.values(seriesMap);
    },

    // group the data throughout the day
    groupStackedData(data) {
      let groupedData = [];
      // loop through all the data
      data.map((date) => {
        // reduce each category each day and sum the totals
        // return only the values not the date key
        let reducedDate = Object.values(
          date.reduce((acc, item) => {
            let key = item.key[1];
            if (!acc[key]) {
              acc[key] = { key: key, value: 0 }; // make an empty spot for new cats
            }
            // accumulate the valyes
            acc[key].value += item.value;
            return acc;
          }, {}),
        );
        // push the reduced data to the grouped data array
        groupedData.push(reducedDate);
      });
      return groupedData;
    },

    groupMonthData(monthsData) {
      let cats = [];
      monthsData.map((day) => {
        day.map((data) => {
          const existing = cats.find((c) => c.key === data.key);
          if (!existing) {
            cats.push({ key: data.key, value: data.value });
          } else {
            // find the correct category and add the value
            existing.value += data.value;
          }
        });
      });
      return cats;
    },

    getStackedTotals(data) {
      const flattened = this.period !== "day" ? data.flatMap((d) => d.value) : data;
      const totals = {};

      flattened.forEach((d) => {
        const key = this.period === "day" ? d.key[1] : d.key;
        totals[key] = (totals[key] || 0) + d.value;
      });



      return Object.entries(totals).map(([key, value]) => ({ key, value }));
    },

    groupDataByDay(data, dates) {
      let groupedData = [];
      dates[0].forEach((d) => {
        groupedData.push({ key: d, value: [] });
      });

      // this needs to be utc, so look at the hours in the day as well as date, like T11 - T11??

      // ensure we only attach items whose date AND hour match the day's allowed hour
      const timezoneOffset = reports.getTimeZoneOffset();

      data.forEach((data1) => {
        // data1.key[0] expected like 'YYYY-MM-DDTHH...' -> extract date and hour
        const dataDatePart = data1.key[0].substring(0, 10);
        const rawHour = Number(data1.key[0].substring(11, 13));
        if (Number.isNaN(rawHour)) return;

        // convert to local hour same way other helpers do
        let adjustedHour = rawHour + timezoneOffset;
        if (adjustedHour >= 24) adjustedHour -= 24;
        if (adjustedHour < 0) adjustedHour += 24;

        // iterate each date-range in dates[0] (expected format: [startStr, endStr])
        dates[0].forEach((dateRange, idx) => {
          const rangeStart = dateRange[0] || ""; // e.g. 'YYYY-MM-DDTHH'
          const rangeDatePart = rangeStart.substring(0, 10);
          // extract hour from range start (defensive)
          const rangeHourStr = rangeStart.includes("T") ? rangeStart.split("T")[1] : rangeStart.substring(11, 13);
          const rangeHour = Number(rangeHourStr);

          // only push when both the calendar day and the hour match
          if (dataDatePart === rangeDatePart && !Number.isNaN(rangeHour) && adjustedHour === rangeHour) {
            groupedData[idx].value.push(data1);
          }
        });
      });

      return groupedData;
    },

    groupStackedProducts(data) {
      let prods = Object.fromEntries(this.stackedTotals.map((i) => [i.key, []]));
      data.forEach((item) => {
        const key = item.key[1];
        if (prods[key]) {
          prods[key].push(item);
        }
      });
      let cats = [];
      data.map((p) => {
        const product = p.key[2];
        const category = p.key[1];

        let index = cats.findIndex((c) => c.key === product);
        if (index === -1) {
          cats.push({
            key: product,
            value: p.value,
            category: category,
          });
        } else {
          cats[index].value += p.value;
        }
      });

      const grouped = {};

      cats.forEach((item) => {
        const category = item.category;
        const key = item.key;
        const value = item.value;

        // create the category object if it doesn't exist
        if (!grouped[category]) {
          grouped[category] = {};
        }

        // add the key-value pair to the category
        grouped[category][key] = value;
      });

      // this.overallTotal = Object.values(totalCheck).reduce((sum, val) => sum + val, 0);

      let totalCheck = {};
      Object.entries(grouped).forEach(([cat, prods]) => {
        Object.entries(prods).forEach(([prod, val]) => {
          totalCheck[cat] = (totalCheck[cat] || 0) + val;
        });
      });

      this.totalCheck = totalCheck; // Store for debugging
      this.overallTotal = Object.values(totalCheck).reduce((sum, val) => sum + val, 0);
      return grouped;
    },

    // groupStackedProducts(data) {
    //   const grouped = {};
    //   data.forEach((item) => {
    //     const [, category, product] = item.key;
    //     const value = Number(item.value) || 0;
    //     grouped[category] ??= {};
    //     grouped[category][product] = (grouped[category][product] || 0) + value;
    //   });
    //   return grouped;
    // },

    sortProducts(order, category) {
      // category: string key (category name)
      const productsObj = this.stackedProducts[category];
      if (!productsObj || typeof productsObj !== "object") return;
      const entries = Object.entries(productsObj);
      const sorted = entries.sort((a, b) => {
        const aVal = Number(a[1]) || 0;
        const bVal = Number(b[1]) || 0;
        return order === "asc" ? aVal - bVal : bVal - aVal;
      });
      this.$set(this.stackedProducts, category, Object.fromEntries(sorted));
    },

    // what if there is no data?
    async collectData() {
      if (this.dateFound) {
        if (this.period == "day") {
          let total = await reports.fetchTotals(this.day[0], this.day[1], this.dataType, this.companyName, this.site);
          this.overallTotal = total[0].value;

          this.stackedProducts = [];
          this.stackedData = await reports.fetchRawStackedData(this.day[0], this.day[1], this.dataType, this.companyName, this.site);
          this.series = this.populateDayData(this.stackedData);
          this.catAxis = this.labelAxis(this.stackedData);
          this.stackedTotals = this.getStackedTotals(this.stackedData);
          let data = await reports.fetchRawStackedProductData(this.day[0], this.day[1], this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(data);
        } else if (this.period == "week") {
          this.stackedProducts = [];
          this.stackedTotals = [];
          // get the dates that are in the week
          let datesInWeek = reports.getWeekDates(this.day[0]);
          let fixedDays = [];
          let days = datesInWeek;
          let labels = [];
          // loop through all the dates and get the number for the label
          datesInWeek.forEach((date) => {
            labels.push(reports.formatFromDate(date).substring(8, 10));
          });

          let timezoneOffset = reports.getTimeZoneOffset();
          // loop through the days and get range
          days.forEach((date) => {
            let shiftedDate = reports.shiftHours(date, -timezoneOffset);
            let shiftedEnd = reports.shiftHours(shiftedDate, 24);

            let hours = shiftedDate.getHours();

            // the start and end of each day, use utc rather then nz time
            let newStart = reports.formatFromDate(shiftedDate) + "T" + hours;
            let newEnd = reports.formatFromDate(shiftedEnd) + "T" + hours;

            // and put the start of the day and end into the array for data collection
            fixedDays.push([newStart, newEnd]);
          });

          // store all the raw data here
          let allData = [];
          // loop through how many days we have and collect the data
          for (let i = 0; i < fixedDays.length; i++) {
            let daysData = await reports.fetchRawStackedData(fixedDays[i][0], fixedDays[i][1], this.dataType, this.companyName, this.site);
            allData.push(daysData);
          }

          let start = fixedDays[0][0];
          let end = fixedDays[fixedDays.length - 1][0];
          let productData = await reports.fetchRawStackedProductData(start, end, this.dataType, this.companyName, this.site);

          // group that data by the day to get overall amounts
          let groupedData = this.groupStackedData(allData);
          this.stackedProducts = this.groupStackedProducts(productData);

          // match the grouped data with the date
          let matchedData = [];
          for (let i = 0; i < days.length; i++) {
            matchedData[i] = { key: days[i], value: groupedData[i] };
          }

          this.series = this.populateData(matchedData);
          this.catAxis = labels;
          this.stackedTotals = this.getStackedTotals(matchedData);
          let total = await reports.fetchTotals(start, end, this.dataType, this.companyName, this.site);
          this.overallTotal = total[0].value;
        } else if (this.period == "month") {
          this.stackedProducts = [];
          this.loading = true;

          // move the date so that it is not in the last month? Is there a better way to do this
          let movedDate = dayRange.editDay(3, new Date(this.day[0].substring(0, 4), this.day[0].substring(5, 7) - 1, this.day[0].substring(8, 10)));
          // get the first day of month
          let date = dayRange.startOfMonth(movedDate);
          // get the name of the month??
          // let monthName = date.toLocaleString("default", { month: "long" });

          // get all the days of the month
          let unixDate = Date.parse(date);
          let daysInMonth = dayRange.getNumberOfDaysInMonth(unixDate);
          let labels = [];
          let dates = [];
          let timezoneOffset = reports.getTimeZoneOffset();

          // loop through each day of the month and collect the number and array for start and end of day
          for (let i = 0; i < daysInMonth; i++) {
            labels.push(i + 1);
            let fullDate = dayRange.editDay(i, date);
            let movedStart = reports.shiftHours(fullDate, -timezoneOffset);
            let movedEnd = reports.shiftHours(movedStart, 24);

            let hours = movedStart.getHours();

            let newStart = reports.formatFromDate(movedStart) + "T" + hours;
            let newEnd = reports.formatFromDate(movedEnd) + "T" + hours;

            dates.push([newStart, newEnd]);
          }

          // get the raw data for every day in the month
          let allData = [];
          for (let i = 0; i < dates.length; i++) {
            let daysData = await reports.fetchRawStackedData(dates[i][0], dates[i][1], this.dataType, this.companyName, this.site);
            allData.push(daysData);
          }

          let productData = await reports.fetchRawStackedProductData(dates[0][0], dates[dates.length - 1][0], this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(productData);

          // group the data by day rather than hour
          let groupedData = this.groupStackedData(allData);
          let matchedData = [];

          for (let i = 0; i < dates.length; i++) {
            matchedData[i] = { key: dates[i], value: groupedData[i] };
          }

          this.series = this.populateData(matchedData);
          this.catAxis = labels;
          this.stackedTotals = this.getStackedTotals(matchedData);
          let total = await reports.fetchTotals(dates[0][0], dates[dates.length - 1][0], this.dataType, this.companyName, this.site);
          this.overallTotal = total[0].value;
        } else if (this.period == "year") {
          this.stackedProducts = [];
          this.loading = true;
          let start = dayRange.startOfMonth(new Date(this.day[0], 0, 1));
          let end = dayRange.endOfMonth(new Date(this.day[0], 11, 30));
          let dates = reports.allDatesInRange(start, end);

          let allData = await reports.fetchRawStackedData(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);
          let dailyData = this.groupDataByDay(allData, dates);
          let flattenedData = dailyData.map((d) => d.value);
          let groupedData = this.groupStackedData(flattenedData);

          let index = 0;
          let monthData = [];

          for (let i = 0; i < 12; i++) {
            let date = new Date();
            date.setMonth(i);
            let daysInMonth = dayRange.getNumberOfDaysInMonth(date);

            // ensure the array exists
            monthData[i] = [];
            for (let e = index; e < index + daysInMonth; e++) {
              monthData[i].push(groupedData[e]);
            }
            index += daysInMonth;
          }

          let grouped = [];
          monthData.map((month) => {
            let mdata = this.groupMonthData(month);
            // grouped.push({key: month, value: mdata});
            grouped.push(mdata);
          });

          // need to put the month on the start of the grouped data
          let months = reports.monthLabels();
          for (let i = 0; i <= 11; i++) {
            grouped[i] = { key: months[i], value: grouped[i] };
          }

          // add the month to the grouped data
          this.series = this.populateData(grouped);
          this.catAxis = reports.monthLabels();
          this.stackedTotals = this.getStackedTotals(grouped);
          let productData = await reports.fetchRawStackedProductData(dates[0][0], dates[dates.length - 1][0], this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(productData);

          let total = await reports.fetchTotals(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);
          this.overallTotal = total[0].value;
        }
        this.loading = false;
      }
    },

    async collectRangeData() {
      if (this.dateFound) {
        if (this.period == "dayRange") {
          // dates begin at T00 of first day and T00 of day after

          let end = dayRange.endTime(dayRange.editDay(-1, new Date(this.day[1].substring(0, 4), this.day[1].substring(5, 7) - 1, this.day[1].substring(8, 10))));
          let allData = await reports.fetchRawStackedData(this.day[0], end, this.dataType, this.companyName, this.site);

          let dates = reports.allDatesInRange(this.day[0], end);
          let dailyData = this.groupDataByDay(allData, dates);
          const result = dailyData.map((d) => d.value);
          // need to have the data already in days
          let groupedData = this.groupStackedData(result);

          // match the grouped data with the dates
          let dataWithDates = [];
          for (let i = 0; i < dates[1].length; i++) {
            dataWithDates.push({ key: dates[1][i], value: groupedData[i] });
          }

          this.series = this.populateData(dataWithDates);
          this.catAxis = reports.dayRangeLabels(dates[1]);
          this.stackedTotals = this.getStackedTotals(dataWithDates);

          let productData = await reports.fetchRawStackedProductData(this.day[0], this.day[1], this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(productData);
          
        } else if (this.period == "weekRange") {
          //get the start and end of the range
          // this is hour 0 of the starting monday, and hour 0 of the monday following the last week
          let start = dayRange.startOfWeek(dayRange.getDateOfWeek(this.day[0].substring(6, 8), this.day[0].substring(0, 4)));
          let end = dayRange.endOfWeek(dayRange.getDateOfWeek(this.day[1].substring(6, 8), this.day[1].substring(0, 4)));

          // get all the data
          let allData = await reports.fetchRawStackedData(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);

          let dates = reports.allDatesInRange(start, end);
          let dailyData = this.groupDataByDay(allData, dates);
          const result = dailyData.map((d) => d.value);
          let groupedData = this.groupStackedData(result);

          // match the grouped data with the dates
          let dataWithDates = [];
          for (let i = 0; i < dates[1].length; i++) {
            dataWithDates.push({ key: dates[1][i], value: groupedData[i] });
          }

          let weeklyData = [];

          dataWithDates.forEach((item) => {
            let week = dayRange.getWeekFromDate(item.key);

            // Find existing entry
            let existing = weeklyData.find((w) => w.key === week);

            if (!existing) {
              // Create new week entry
              weeklyData.push({ key: week, value: [...item.value] });
            } else {
              // Add to existing week entry
              existing.value.push(...item.value);
            }
          });

          this.series = this.populateData(weeklyData);
          this.catAxis = dayRange.weekLabels(dates[1]);
          this.stackedTotals = this.getStackedTotals(dataWithDates);

          let productData = await reports.fetchRawStackedProductData(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(productData);
        } else if (this.period == "monthRange") {
          //get the start and end of the range
          let start = dayRange.startOfMonth(new Date(this.day[0].substring(0, 4), this.day[0].substring(5, 7)) - 1);
          let end = dayRange.endOfMonth(new Date(this.day[1].substring(0, 4), this.day[1].substring(5, 7) - 1));

          // get all the data
          let allData = await reports.fetchRawStackedData(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);
          let dates = reports.allDatesInRange(start, end);

          let dailyData = this.groupDataByDay(allData, dates);
          const result = dailyData.map((d) => d.value);
          let groupedData = this.groupStackedData(result);

          let dataWithDates = [];
          for (let i = 0; i < dates[1].length; i++) {
            dataWithDates.push({ key: dates[1][i], value: groupedData[i] });
          }
          let monthlyData = [];

          dataWithDates.forEach((item) => {
            let month = item.key.getMonth();

            // Find existing entry
            let existing = monthlyData.find((w) => w.key === month);

            if (!existing) {
              // Create new week entry
              monthlyData.push({ key: month, value: [...item.value] });
            } else {
              // Add to existing week entry
              existing.value.push(...item.value);
            }
          });

          this.series = this.populateData(monthlyData);
          let months = reports.getMonthsWithinRange(this.day[0], this.day[1]);
          this.catAxis = months[0];
          this.stackedTotals = this.getStackedTotals(dataWithDates);
          let productData = await reports.fetchRawStackedProductData(dayRange.formatDate(start), dayRange.formatDate(end), this.dataType, this.companyName, this.site);
          this.stackedProducts = this.groupStackedProducts(productData);
        }
      }
    },

    handleChange() {
      // for when the date or period changes
      this.dateFound = true;
      const d = this.day[0];
      switch (this.period) {
        case "day":
          this.dayString = dayRange.displayDate(this.day[1]);
          this.collectData();
          break;
        case "week":
          this.collectData();
          this.weekString = `Week ${d.substring(6, 8)}, ${d.substring(0, 4)}`;
          break;
        case "month":
          this.collectData();
          const months = reports.monthLabels();
          let monthNum = this.day[0].substring(5, 7);
          this.monthString = months[monthNum];
          break;
        case "year":
          this.collectData();
          this.yearString = d instanceof Date && !isNaN(d.getTime()) ? d.getFullYear() : String(d);
          break;
        default:
          this.collectRangeData();
      }
    },
  },

  watch: {
    site: function () {
      this.collectData();
      this.collectRangeData();
    },
    dataType: function () {
      this.yName = this.dataType == "sales" ? "$" : "Units Sold";
      this.collectData();
    },
    day: function () {
      this.handleChange();
    },
    period: function () {
      this.handleChange();
    },
  },

  mounted() {
    this.handleChange();
  },
};
</script>

<style scoped lang="scss">
.chart-container {
  min-height: 60vh;
  // width: 60vw;
  width: 80vw;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  z-index: 0;

  .loading {
    display: flex;
    height: 60vh;
    width: 80vw;
    align-items: center;
    justify-content: center;
    background-color: var(--opacity-background);
    z-index: 1000;
    position: absolute;
  }

  .stackedTotals {
    .stacked-list {
      .stacked-item {
        .summary-row {
          padding: 0.5rem;
          background-color: var(--primary-colour);
          border-radius: 0.25rem;
          margin: 0.25rem;
          display: flex;
          justify-content: space-between;
        }

        .expanded-content {
          display: flex;
          flex-direction: row-reverse;
          .sorting-icons {
            display: flex;
            flex-direction: column;
            margin-top: 1rem;
          }
          table {
            margin: 1rem;
            // border: 1px solid black;
            thead {
              text-transform: bold;
            }
            tbody {
              background-color: var(--primary-colour);
              td {
                padding: 0.5rem;
                border-radius: 0.25rem;
              }
            }
          }
        }
      }
    }
  }
  .stacked-chart {
    width: 100%;
    height: 100%;
    .chart {
      max-height: 120vh;
    }
  }
}

@media only screen and (orientation: portrait) {
  .chart-container {
    width: 100%;
    min-height: 100vh;
    padding: 2.55px;
    flex-direction: column;

    .stackedTotals {
      max-width: 100%;
      flex-direction: column;
      display: flex;
      margin-bottom: 1rem;
      h2 {
        margin: 1rem;
      }
      .stacked-list {
        .stacked-item {
          details {
            margin-left: 0;
          }
          .summary-row {
            max-width: 100% !important;
          }

          .expanded-content {
            margin-left: 0;
            .inner-table {
              min-width: 90%;
            }
          }
        }
      }
    }

    // .stacked-chart {
    // }
  }
}
</style>
